import { useState, useEffect, useMemo, useRef } from 'react';

export interface ComboboxOption {
    value: string;
    label: string;
    icon?: React.ReactNode;
    [key: string]: any;
}

interface UseAutocompleteProps {
    options: ComboboxOption[];
    value?: string; // The selected value (ID)
    onChange?: (value: string) => void; // Called when a selection is made
    onInputChange?: (value: string) => void; // Called when text changes
    placeholder?: string;
    disabled?: boolean;
    initialInputValue?: string;
    /**
     * If true, the input value will be reset to the selected option's label
     * when the dropdown closes or when the value prop changes.
     * If false (free text mode), the input value remains as typed.
     */
    enforceSelection?: boolean;
    onSelect?: (option: ComboboxOption) => void;
}

export const useAutocomplete = ({
    options,
    value,
    onChange,
    onInputChange,
    onSelect,
    disabled = false,
    enforceSelection = true,
}: UseAutocompleteProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(0);

    // Refs
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    // Get selected option object derived from value prop
    const selectedOption = useMemo(() =>
        options.find(o => o.value === value),
        [options, value]);

    // Update input text when external value changes or when menu closes (if enforcing selection)
    useEffect(() => {
        if (enforceSelection) {
            if (!isOpen && selectedOption) {
                setInputValue(selectedOption.label);
            } else if (!isOpen && !selectedOption && value) {
                // Value exists but option not found? Keep empty or maybe value?
            } else if (!isOpen && !selectedOption && !value) {
                if (inputValue !== '') setInputValue('');
            }
        }
    }, [value, selectedOption, isOpen, enforceSelection]);

    // Filter options
    const filteredOptions = useMemo(() => {
        // If enforcing selection and input matches selected label, show all
        if (enforceSelection && selectedOption && inputValue === selectedOption.label) {
            return options;
        }

        // If input is empty, show all
        if (!inputValue) return options;

        const lower = inputValue.toLowerCase();
        return options.filter(option =>
            option.label.toLowerCase().includes(lower)
        );
    }, [options, inputValue, selectedOption, enforceSelection]);

    // Reset highlight when input changes
    useEffect(() => {
        setHighlightedIndex(0);
    }, [inputValue]);

    // Ensure highlighted index is within bounds
    useEffect(() => {
        if (highlightedIndex >= filteredOptions.length && filteredOptions.length > 0) {
            setHighlightedIndex(0);
        }
    }, [filteredOptions, highlightedIndex]);

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Scroll highlighted into view
    useEffect(() => {
        if (isOpen && listRef.current && filteredOptions.length > 0) {
            const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
            if (highlightedElement) {
                highlightedElement.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [highlightedIndex, isOpen, filteredOptions]);

    const handleInputFocus = () => {
        if (!disabled) setIsOpen(true);
    };

    const handleInputChangeInternal = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newVal = e.target.value;
        setInputValue(newVal);
        setIsOpen(true);
        onInputChange?.(newVal);
    };

    const handleOptionSelect = (option: ComboboxOption) => {
        onChange?.(option.value);
        onSelect?.(option);

        // Always fill input with label on selection
        setInputValue(option.label);

        setIsOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setIsOpen(true);
                setHighlightedIndex(prev => (prev + 1) % filteredOptions.length);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setIsOpen(true);
                setHighlightedIndex(prev => (prev - 1 + filteredOptions.length) % filteredOptions.length);
                break;
            case 'Enter':
                e.preventDefault(); // Prevent form submission
                if (isOpen && filteredOptions[highlightedIndex]) {
                    handleOptionSelect(filteredOptions[highlightedIndex]);
                } else if (!enforceSelection) {
                    // In free text mode, Enter implies submitting the current text.
                    setIsOpen(false);
                } else {
                    setIsOpen(true);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                inputRef.current?.blur();
                break;
            case 'Tab':
                setIsOpen(false);
                break;
        }
    };

    const clearSelection = (e: React.MouseEvent) => {
        e.stopPropagation();
        setInputValue('');
        onChange?.(''); // Notify parent of clearance
        setIsOpen(true);
        inputRef.current?.focus();
    };

    const toggleOpen = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!disabled) {
            if (isOpen) {
                setIsOpen(false);
            } else {
                setIsOpen(true);
                inputRef.current?.focus();
            }
        }
    };

    return {
        isOpen,
        setIsOpen,
        inputValue,
        setInputValue,
        highlightedIndex,
        setHighlightedIndex,
        filteredOptions,
        selectedOption,
        containerRef,
        inputRef,
        listRef,
        handleInputFocus,
        handleInputChange: handleInputChangeInternal,
        handleOptionSelect,
        handleKeyDown,
        clearSelection,
        toggleOpen
    };
};
